/* ──────────────────────────────────────────────────────────────────────────
 * Operational data layer — crowd density (§2) and incidents (§3).
 *
 * ⚠ ALL FIGURES IN THIS FILE ARE SIMULATED. No sensor, CCTV or department
 * feed is connected to this POC. The numbers are deterministic (seeded, not
 * random) so the demo is reproducible and nobody mistakes a reroll for a
 * changing situation.
 *
 * The shape of these records is the contract a real feed must satisfy. When
 * an actual crowd-counting or incident system is integrated, it populates the
 * same structures through the adapters in geo/services/ingest.js and nothing
 * downstream changes.
 * ────────────────────────────────────────────────────────────────────────── */

import { withProvenance, TRUST, SOURCE_KIND, CLASSIFICATION } from "./provenance";
import { SECTORS, sectorStatus } from "./kumbhGeo";

/* Deterministic pseudo-random in [0,1) from an integer seed. Mulberry32 —
 * small, fast, and crucially repeatable, so the same demo shows the same
 * picture to every viewer. */
function seeded(seed) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6D2B79F5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const simProv = (note, classification = CLASSIFICATION.PUBLIC) => ({
  source: SOURCE_KIND.SIMULATION,
  trust: TRUST.SIMULATED,
  confidence: null,
  owner: "InQAi POC",
  classification,
  observedAt: null,
  note: note || "Simulated. No sensor feed is connected.",
});

/* ── Crowd density points ──────────────────────────────────────────────
 * A heatmap needs many weighted points, not six. We scatter points inside
 * each sector, weighted by that sector's occupancy ratio, so the heat
 * genuinely reflects the zone status rather than being decorative. */
export function buildCrowdPoints() {
  const points = [];
  SECTORS.forEach((sector, si) => {
    const rnd = seeded(1000 + si * 37);
    const ratio = sector.current / sector.capacity;
    // Denser sectors get more sample points AND higher per-point weight, so
    // the heatmap separates a busy zone from a full one.
    const count = Math.round(18 + ratio * 44);
    const [cx, cy] = sector.centre;

    for (let i = 0; i < count; i += 1) {
      // Gaussian-ish clustering toward the sector centre via summed uniforms.
      const jx = (rnd() + rnd() + rnd() - 1.5) * 0.010;
      const jy = (rnd() + rnd() + rnd() - 1.5) * 0.007;
      points.push({
        coord: [cx + jx, cy + jy],
        weight: Math.min(1, ratio * (0.55 + rnd() * 0.65)),
        sector: sector.id,
      });
    }
  });
  return points;
}

/* ── Zone-level crowd status ───────────────────────────────────────────── */
export function buildZoneStatus() {
  return SECTORS.map((s) =>
    withProvenance(
      {
        id: s.id,
        name: s.name,
        centre: s.centre,
        capacity: s.capacity,
        current: s.current,
        ratio: s.current / s.capacity,
        status: sectorStatus(s),
      },
      simProv("Occupancy is simulated. A real deployment sources this from " +
              "crowd-counting sensors or CCTV analytics.")
    )
  );
}

/* ── Pedestrian flow ───────────────────────────────────────────────────
 * Bearing in degrees; the map renders these as oriented arrows. */
export const FLOW_VECTORS = [
  { id: "FLOW-01", from: "SEC-01", coord: [73.7910, 20.0005], bearing: 35,  rate: 4200 },
  { id: "FLOW-02", from: "SEC-02", coord: [73.7965, 20.0095], bearing: 210, rate: 3100 },
  { id: "FLOW-03", from: "SEC-03", coord: [73.5315, 19.9415], bearing: 95,  rate: 2600 },
  { id: "FLOW-04", from: "SEC-04", coord: [73.8115, 20.0205], bearing: 260, rate: 1900 },
  { id: "FLOW-05", from: "SEC-05", coord: [73.8380, 19.9490], bearing: 315, rate: 2300 },
].map((f) => withProvenance(f, simProv("Flow direction and rate are simulated.")));

/* ── Incident taxonomy (§3) ────────────────────────────────────────────── */
export const INCIDENT_TYPES = {
  medical:        { label: { en: "Medical emergency",   mr: "वैद्यकीय आणीबाणी" },   color: "#E11D48", icon: "HeartPulse" },
  fire:           { label: { en: "Fire",                mr: "आग" },                  color: "#DC2626", icon: "Flame" },
  missing:        { label: { en: "Missing person",      mr: "हरवलेली व्यक्ती" },     color: "#CA8A04", icon: "UserSearch" },
  security:       { label: { en: "Security incident",   mr: "सुरक्षा घटना" },        color: "#1D4ED8", icon: "Shield" },
  crowdSurge:     { label: { en: "Crowd surge",         mr: "गर्दीची लाट" },         color: "#EA580C", icon: "Users" },
  traffic:        { label: { en: "Traffic congestion",  mr: "वाहतूक कोंडी" },        color: "#D97706", icon: "Navigation" },
  infrastructure: { label: { en: "Infrastructure issue",mr: "पायाभूत सुविधा समस्या" },color: "#475569", icon: "Server" },
  weather:        { label: { en: "Weather-related",     mr: "हवामानविषयक" },         color: "#0891B2", icon: "Cloud" },
  unclassified:   { label: { en: "Unclassified alert",  mr: "अवर्गीकृत सूचना" },     color: "#64748B", icon: "AlertCircle" },
};

export const SEVERITY = {
  critical: { label: { en: "Critical", mr: "गंभीर" }, rank: 3, color: "#DC2626" },
  high:     { label: { en: "High",     mr: "उच्च" },  rank: 2, color: "#EA580C" },
  moderate: { label: { en: "Moderate", mr: "मध्यम" }, rank: 1, color: "#D97706" },
  low:      { label: { en: "Low",      mr: "कमी" },   rank: 0, color: "#0891B2" },
};

/* Incidents. Minutes-ago is converted to a real timestamp at module load so
 * the "last updated" ages naturally while the demo is open. */
const now = Date.now();
const at = (minsAgo) => new Date(now - minsAgo * 60000).toISOString();

export const INCIDENTS = [
  { id: "INC-2027-0412", type: "crowdSurge", severity: "critical", status: "Active",
    coord: [73.7893, 19.9982], sector: "SEC-01",
    location: { en: "Ramkund Ghat — Entry 2", mr: "रामकुंड घाट — प्रवेशद्वार 2" },
    assigned: { en: "Nashik City Police — Zone 1", mr: "नाशिक शहर पोलीस — झोन 1" },
    response: { en: "Units dispatched, diversion in progress", mr: "पथके रवाना, वळण सुरू" },
    reportedAt: at(2), updatedAt: at(1), evidence: "Operator report (simulated)" },

  { id: "INC-2027-0411", type: "medical", severity: "high", status: "Active",
    coord: [73.7961, 20.0102], sector: "SEC-02",
    location: { en: "Panchavati — Water Point 8", mr: "पंचवटी — जल केंद्र 8" },
    assigned: { en: "Health Dept — Medical Camp 2", mr: "आरोग्य विभाग — वैद्यकीय शिबिर 2" },
    response: { en: "Ambulance en route", mr: "रुग्णवाहिका मार्गस्थ" },
    reportedAt: at(6), updatedAt: at(3), evidence: "Operator report (simulated)" },

  { id: "INC-2027-0409", type: "traffic", severity: "moderate", status: "Active",
    coord: [73.8205, 19.9598], sector: "SEC-05",
    location: { en: "Nashik Road corridor", mr: "नाशिक रोड मार्गिका" },
    assigned: { en: "Traffic Police — Nashik Road", mr: "वाहतूक पोलीस — नाशिक रोड" },
    response: { en: "Diversion DIV-01 under consideration", mr: "वळण DIV-01 विचाराधीन" },
    reportedAt: at(11), updatedAt: at(8), evidence: "Operator report (simulated)" },

  { id: "INC-2027-0407", type: "missing", severity: "high", status: "In progress",
    coord: [73.5312, 19.9408], sector: "SEC-03",
    location: { en: "Trimbakeshwar — Kushavarta", mr: "त्र्यंबकेश्वर — कुशावर्त" },
    assigned: { en: "Lost & Found Centre — Trimbak", mr: "हरवले-सापडले केंद्र — त्र्यंबक" },
    response: { en: "Description circulated to all posts", mr: "वर्णन सर्व चौक्यांना पाठवले" },
    reportedAt: at(18), updatedAt: at(12), evidence: "Help desk entry (simulated)" },

  { id: "INC-2027-0404", type: "infrastructure", severity: "moderate", status: "Active",
    coord: [73.8120, 20.0208], sector: "SEC-04",
    location: { en: "Tapovan — barricade section 4", mr: "तपोवन — बॅरिकेड विभाग 4" },
    assigned: { en: "NMC Works", mr: "महापालिका बांधकाम" },
    response: { en: "Repair team assigned", mr: "दुरुस्ती पथक नेमले" },
    reportedAt: at(26), updatedAt: at(20), evidence: "Field officer report (simulated)" },

  { id: "INC-2027-0398", type: "weather", severity: "low", status: "Monitoring",
    coord: [73.6845, 20.0172], sector: "SEC-06",
    location: { en: "Gangapur staging area", mr: "गंगापूर स्टेजिंग क्षेत्र" },
    assigned: { en: "District Control Room", mr: "जिल्हा नियंत्रण कक्ष" },
    response: { en: "Heat advisory issued", mr: "उष्णतेचा इशारा जारी" },
    reportedAt: at(44), updatedAt: at(30), evidence: "Advisory (simulated)" },

  { id: "INC-2027-0396", type: "security", severity: "moderate", status: "Resolved",
    coord: [73.7925, 20.0030], sector: "SEC-01",
    location: { en: "Gandhi Talav Ghat", mr: "गांधी तलाव घाट" },
    assigned: { en: "Nashik City Police — Zone 1", mr: "नाशिक शहर पोलीस — झोन 1" },
    response: { en: "Closed — no further action", mr: "बंद — पुढील कारवाई नाही" },
    reportedAt: at(72), updatedAt: at(52), evidence: "Operator report (simulated)" },
].map((i) =>
  withProvenance(i, {
    ...simProv(
      "This incident is fabricated for demonstration. It does not describe a " +
      "real event, and the assigned authority has not been contacted."
    ),
    observedAt: i.reportedAt,
    updatedAt: i.updatedAt,
  })
);

/* Active = anything not resolved. Used by the right panel and the KPIs. */
/* Density at the incident's sector when it was raised. Captured so the
   VERIFY stage has a before-measurement; without one, an intervention can
   never be shown to have worked. Simulated, like everything around it. */
export const BASELINE_DENSITY = {
  "SEC-01": 3.54, "SEC-02": 1.69, "SEC-03": 4.24,
  "SEC-04": 0.83, "SEC-05": 1.82, "SEC-06": 0.41,
};

export const activeIncidents = () =>
  INCIDENTS.filter((i) => i.status !== "Resolved");

export function incidentsBySeverity() {
  return [...INCIDENTS].sort(
    (a, b) => SEVERITY[b.severity].rank - SEVERITY[a.severity].rank
  );
}
